import { StyleSheet, View } from "react-native";

import {
  AppLink,
  AppText,
  Card,
  Grid,
  GridItem,
  MetricCard,
  ProtectedScreen,
  Row,
  Stack,
} from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceInvoiceReminders,
  useWorkspaceInvoiceLifecycle,
  useWorkspaceInvoices,
  useWorkspaceExpenses,
  useWorkspaceProjects,
  useWorkspaceTasks,
  useWorkspaceTimeEntries,
} from "~/hooks/workspace";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

const nextActions = [
  { href: "/timer", label: "Start a timer" },
  { href: "/clients", label: "Add a client" },
  { href: "/invoices", label: "Create an invoice" },
] as const;

function isToday(isoDate: string) {
  const value = new Date(isoDate);
  const now = new Date();
  return (
    value.getFullYear() === now.getFullYear() &&
    value.getMonth() === now.getMonth() &&
    value.getDate() === now.getDate()
  );
}

function formatDuration(seconds: number) {
  const hours = seconds / 3600;
  return `${hours.toFixed(hours >= 10 ? 0 : 1)}h`;
}

function billableValue(entry: {
  billable: boolean;
  duration_seconds: number;
  hourly_rate_cents: number | null;
}) {
  if (!entry.billable || !entry.hourly_rate_cents) return 0;
  return Math.round((entry.duration_seconds / 3600) * entry.hourly_rate_cents);
}

function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

export default function DashboardScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const { user } = useAuth();
  const workspace = usePrimaryWorkspace();
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const tasks = useWorkspaceTasks(workspace.data?.id);
  const timeEntries = useWorkspaceTimeEntries(workspace.data?.id);
  const invoices = useWorkspaceInvoices(workspace.data?.id);
  const expenses = useWorkspaceExpenses(workspace.data?.id);
  const entries = timeEntries.data ?? [];
  const invoiceRecords = invoices.data ?? [];
  const invoiceIds = invoiceRecords.map((invoice) => invoice.id);
  const reminders = useWorkspaceInvoiceReminders(
    workspace.data?.id,
    invoiceIds,
  );
  const invoiceLifecycle = useWorkspaceInvoiceLifecycle(
    workspace.data?.id,
    invoiceIds,
  );
  const clientRecords = clients.data ?? [];
  const projectRecords = projects.data ?? [];
  const taskRecords = tasks.data ?? [];
  const clientById = new Map(
    clientRecords.map((client) => [client.id, client]),
  );
  const invoiceById = new Map(
    invoiceRecords.map((invoice) => [invoice.id, invoice]),
  );
  const projectById = new Map(
    projectRecords.map((project) => [project.id, project]),
  );
  const today = todayIsoDate();
  const trackedToday = entries
    .filter((entry) => isToday(entry.started_at))
    .reduce((total, entry) => total + entry.duration_seconds, 0);
  const unbilledCents = entries
    .filter((entry) => entry.billable && !entry.invoiced_at)
    .reduce((total, entry) => total + billableValue(entry), 0);
  const draftInvoices = invoiceRecords.filter(
    (invoice) => invoice.status === "draft",
  ).length;
  const overdueInvoices = invoiceRecords.filter(
    (invoice) =>
      !invoice.paid_at &&
      invoice.status !== "void" &&
      invoice.due_date &&
      invoice.due_date < today,
  ).length;
  const dueTasks = taskRecords.filter(
    (task) =>
      task.due_on &&
      task.due_on <= today &&
      task.status !== "done" &&
      task.status !== "archived",
  );
  const dueReminders = reminders.reminders.filter(
    (reminder) =>
      reminder.status === "scheduled" &&
      reminder.scheduled_for.slice(0, 10) <= today,
  );
  const unbilledByClient = entries
    .filter((entry) => entry.billable && !entry.invoiced_at)
    .reduce<Map<string, number>>((totals, entry) => {
      const clientId =
        entry.client_id ??
        (entry.project_id
          ? projectById.get(entry.project_id)?.client_id
          : null);
      if (!clientId) return totals;
      totals.set(clientId, (totals.get(clientId) ?? 0) + billableValue(entry));
      return totals;
    }, new Map());
  const clientFocus = [...unbilledByClient.entries()]
    .sort((left, right) => right[1] - left[1])
    .slice(0, 3);
  const unbilledByProject = entries
    .filter((entry) => entry.billable && !entry.invoiced_at && entry.project_id)
    .reduce<Map<string, number>>((totals, entry) => {
      const projectId = entry.project_id ?? "";
      totals.set(
        projectId,
        (totals.get(projectId) ?? 0) + billableValue(entry),
      );
      return totals;
    }, new Map());
  const projectFocus = projectRecords
    .filter((project) => !project.archived)
    .sort(
      (left, right) =>
        (unbilledByProject.get(right.id) ?? 0) -
        (unbilledByProject.get(left.id) ?? 0),
    )
    .slice(0, 3);
  const setAsideEstimate = Math.round(
    unbilledCents * ((workspace.data?.tax_set_aside_percentage ?? 0) / 100),
  );
  const monthPrefix = today.slice(0, 7);
  const cashReceivedThisMonth = invoiceLifecycle.payments
    .filter((payment) => payment.paid_at.slice(0, 7) === monthPrefix)
    .reduce((total, payment) => total + payment.amount_cents, 0);
  const expensesThisMonth = expenses.expenses
    .filter((expense) => expense.expense_date.slice(0, 7) === monthPrefix)
    .reduce(
      (total, expense) => total + (expense.claimable_total_cents ?? 0),
      0,
    );
  const netCashThisMonth = cashReceivedThisMonth - expensesThisMonth;
  const summaryCards: {
    label: string;
    value: string;
    accent: "none" | "mint" | "danger";
  }[] = [
    {
      label: "Tracked today",
      value: formatDuration(trackedToday),
      accent: "none",
    },
    {
      label: "Unbilled work",
      value: formatMoney(unbilledCents),
      accent: "none",
    },
    {
      label: "Draft invoices",
      value: String(draftInvoices),
      accent: "none",
    },
    {
      label: "Overdue invoices",
      value: String(overdueInvoices),
      accent: overdueInvoices > 0 ? "danger" : "none",
    },
    {
      label: "Tax set-aside estimate",
      value: formatMoney(setAsideEstimate),
      accent: "mint",
    },
  ];
  const outstandingInvoices = invoiceRecords
    .filter(
      (invoice) =>
        !invoice.paid_at &&
        invoice.status !== "void" &&
        invoice.status !== "draft",
    )
    .slice(0, 5);

  return (
    <ProtectedScreen
      title="Dashboard"
      subtitle="Time, invoices, and set-aside visibility for NZ freelancers."
    >
      <AppText size="lg" weight="semibold">
        Hello {user?.firstName || "there"}
      </AppText>

      <Grid gap="md">
        {summaryCards.map((card) => (
          <GridItem key={card.label} minWidth={160}>
            <MetricCard
              label={card.label}
              value={card.value}
              accent={card.accent}
            />
          </GridItem>
        ))}
      </Grid>

      <Card gap="sm">
        <Stack gap="xs">
          <AppText size="base" weight="semibold">
            Money this month
          </AppText>
          <AppText size="xs" tone="muted">
            Cash received less recorded business expenses. Planning view only.
          </AppText>
        </Stack>
        <Grid gap="md">
          <GridItem minWidth={180} style={stylesheet.moneySummaryItem}>
            <Stack gap="xs">
              <AppText size="xs" tone="muted">
                Cash received
              </AppText>
              <AppText size="lg" weight="bold">
                {formatMoney(cashReceivedThisMonth)}
              </AppText>
            </Stack>
          </GridItem>
          <GridItem minWidth={180} style={stylesheet.moneySummaryItem}>
            <Stack gap="xs">
              <AppText size="xs" tone="muted">
                Business expenses
              </AppText>
              <AppText size="lg" weight="bold">
                {formatMoney(expensesThisMonth)}
              </AppText>
            </Stack>
          </GridItem>
          <GridItem minWidth={180} style={stylesheet.moneySummaryItem}>
            <Stack gap="xs">
              <AppText size="xs" tone="muted">
                Net cash
              </AppText>
              <AppText
                size="lg"
                weight="bold"
                tone={netCashThisMonth < 0 ? "danger" : "default"}
              >
                {formatMoney(netCashThisMonth)}
              </AppText>
            </Stack>
          </GridItem>
        </Grid>
      </Card>

      <View style={stylesheet.reportGrid}>
        <Card gap="md">
          <Stack gap="xs">
            <AppText size="base" weight="semibold">
              Outstanding invoices
            </AppText>
            <AppText size="xs" tone="muted">
              Sent invoices waiting on payment.
            </AppText>
          </Stack>
          {outstandingInvoices.length === 0 ? (
            <AppText size="sm">No outstanding invoices.</AppText>
          ) : (
            <Stack gap="sm">
              {outstandingInvoices.map((invoice) => (
                <Row
                  key={invoice.id}
                  between
                  gap="md"
                  style={stylesheet.focusRow}
                >
                  <Stack gap="xs" style={stylesheet.focusMain}>
                    <AppText weight="semibold">
                      {invoice.invoice_number}
                    </AppText>
                    <AppText size="xs" tone="muted">
                      {invoice.due_date
                        ? `Due ${invoice.due_date}`
                        : `Status ${invoice.status}`}
                      {" · "}
                      {formatMoney(invoice.total_cents)}
                    </AppText>
                  </Stack>
                  <AppLink href={`/invoices/${invoice.id}` as never} size="xs">
                    View
                  </AppLink>
                </Row>
              ))}
            </Stack>
          )}
        </Card>

        <Card gap="md">
          <Stack gap="xs">
            <AppText size="base" weight="semibold">
              Due now
            </AppText>
            <AppText size="xs" tone="muted">
              Tasks and reminders that need attention today.
            </AppText>
          </Stack>
          <Stack gap="sm">
            <Row between gap="md" style={stylesheet.reportRow}>
              <AppText>Tasks due</AppText>
              <AppText weight="semibold">{String(dueTasks.length)}</AppText>
            </Row>
            <Row between gap="md" style={stylesheet.reportRow}>
              <AppText>Reminders due</AppText>
              <AppText weight="semibold">{String(dueReminders.length)}</AppText>
            </Row>
            <Row between gap="md" style={stylesheet.reportRow}>
              <AppText>Active projects</AppText>
              <AppText weight="semibold">
                {String(
                  projectRecords.filter((project) => !project.archived).length,
                )}
              </AppText>
            </Row>
            {dueTasks.slice(0, 3).map((task) => (
              <Row key={task.id} between gap="md" style={stylesheet.focusRow}>
                <Stack gap="xs" style={stylesheet.focusMain}>
                  <AppText weight="semibold">{task.name}</AppText>
                  <AppText size="xs" tone="muted">
                    {task.due_on ? `Due ${task.due_on}` : "No due date"}
                  </AppText>
                </Stack>
                <AppLink href={`/tasks/${task.id}` as never} size="xs">
                  View
                </AppLink>
              </Row>
            ))}
            {dueReminders.slice(0, 3).map((reminder) => {
              const invoice = invoiceById.get(reminder.invoice_id);
              return (
                <Row
                  key={reminder.id}
                  between
                  gap="md"
                  style={stylesheet.focusRow}
                >
                  <Stack gap="xs" style={stylesheet.focusMain}>
                    <AppText weight="semibold">
                      {invoice?.invoice_number ?? "Invoice reminder"}
                    </AppText>
                    <AppText size="xs" tone="muted">
                      Reminder due {reminder.scheduled_for.slice(0, 10)}
                    </AppText>
                  </Stack>
                  {invoice ? (
                    <AppLink
                      href={`/invoices/${invoice.id}` as never}
                      size="xs"
                    >
                      View
                    </AppLink>
                  ) : null}
                </Row>
              );
            })}
          </Stack>
        </Card>

        <Card gap="md">
          <Stack gap="xs">
            <AppText size="base" weight="semibold">
              Client focus
            </AppText>
            <AppText size="xs" tone="muted">
              Largest unbilled client balances from tracked time.
            </AppText>
          </Stack>
          {clientFocus.length === 0 ? (
            <AppText size="sm">No unbilled client time yet.</AppText>
          ) : (
            <Stack gap="sm">
              {clientFocus.map(([clientId, cents]) => (
                <Row
                  key={clientId}
                  between
                  gap="md"
                  style={stylesheet.focusRow}
                >
                  <Stack gap="xs" style={stylesheet.focusMain}>
                    <AppText weight="semibold">
                      {clientById.get(clientId)?.name ?? "Unknown client"}
                    </AppText>
                    <AppText size="xs" tone="muted">
                      {formatMoney(cents)} unbilled
                    </AppText>
                  </Stack>
                  <AppLink href={`/clients/${clientId}` as never} size="xs">
                    View
                  </AppLink>
                </Row>
              ))}
            </Stack>
          )}
        </Card>

        <Card gap="md">
          <Stack gap="xs">
            <AppText size="base" weight="semibold">
              Project focus
            </AppText>
            <AppText size="xs" tone="muted">
              Open projects with budget or schedule context.
            </AppText>
          </Stack>
          {projectFocus.length === 0 ? (
            <AppText size="sm">No active projects yet.</AppText>
          ) : (
            <Stack gap="sm">
              {projectFocus.map((project) => (
                <Row
                  key={project.id}
                  between
                  gap="md"
                  style={stylesheet.projectRow}
                >
                  <Stack gap="xs" style={stylesheet.projectMain}>
                    <AppText weight="semibold">{project.name}</AppText>
                    <AppText size="xs" tone="muted">
                      {project.client_id
                        ? (clientById.get(project.client_id)?.name ??
                          "Unknown client")
                        : "No client"}
                    </AppText>
                  </Stack>
                  <AppText size="xs" tone="muted">
                    {(unbilledByProject.get(project.id) ?? 0) > 0
                      ? `${formatMoney(unbilledByProject.get(project.id) ?? 0)} unbilled`
                      : project.budget_cents
                        ? formatMoney(project.budget_cents)
                        : project.ends_on
                          ? `Ends ${project.ends_on}`
                          : "No budget"}
                  </AppText>
                  <AppLink href={`/projects/${project.id}` as never} size="xs">
                    View
                  </AppLink>
                </Row>
              ))}
            </Stack>
          )}
        </Card>
      </View>

      <Card gap="md">
        <Stack gap="xs">
          <AppText size="base" weight="semibold">
            Next actions
          </AppText>
          <AppText size="xs" tone="muted">
            Jump back into the actions that keep client work moving.
          </AppText>
        </Stack>
        <Grid gap="md">
          {nextActions.map((action) => (
            <GridItem
              key={action.href}
              minWidth={180}
              style={stylesheet.actionTile}
            >
              <AppLink href={action.href as never} size="sm" underline={false}>
                {action.label}
              </AppLink>
            </GridItem>
          ))}
        </Grid>
      </Card>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    actionTile: {
      minHeight: 52,
      justifyContent: "center",
      paddingHorizontal: spacing.l,
      borderWidth: 1,
      borderColor: colors.hairline,
      borderRadius: radii.control,
      backgroundColor: colors.surfaceSunken,
    },
    projectMain: {
      flex: 1,
      minWidth: 160,
    },
    focusMain: {
      flex: 1,
      minWidth: 160,
    },
    focusRow: {
      minHeight: 44,
      paddingVertical: spacing.s,
      borderBottomWidth: 1,
      borderBottomColor: colors.hairline,
    },
    projectRow: {
      minHeight: 44,
      paddingVertical: spacing.s,
      borderBottomWidth: 1,
      borderBottomColor: colors.hairline,
    },
    reportGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    moneySummaryItem: {
      padding: spacing.l,
      borderRadius: radii.m,
      backgroundColor: colors.surfaceSunken,
    },
    reportRow: {
      minHeight: 38,
      paddingVertical: spacing.s,
      borderBottomWidth: 1,
      borderBottomColor: colors.hairline,
    },
  });
